import Vue from 'vue';
import {
	PanelFilters,
	countPanelFilters,
	normalizePanelFilters,
	samePanelFilters,
} from '@/utils/panelFilters';

/**
 * The Filters box's behaviour, the same on every table that has one: the
 * Directory, a Team's members, a member's Teams, the company's Teams and Boards.
 *
 * The box's fields edit `draftFilters` only, so nothing filters until Apply.
 * Apply commits the draft, closes the box and refetches once, unless nothing
 * changed. Cancel puts the draft back to what is applied and closes. Clear all
 * empties the draft and applies that at once, leaving the box open, as the
 * Library's does. The Filter button counts what is applied; the box counts the
 * draft.
 *
 * The host supplies three things:
 *  - `restingPanelFilters` (computed): the box with nothing narrowing the list;
 *  - `appliedPanelFilters` (computed): the filters the listing was fetched with;
 *  - `commitPanelFilters(next)` (method): make `next` the applied filters,
 *    clear the selection and start the walk over.
 */
export const FiltersPanelMixin = Vue.extend({
	data () {
		return {
			filtersExpanded: false,
			draftFilters: {} as PanelFilters,
		};
	},
	computed: {
		appliedFilterCount (): number {
			return countPanelFilters(this.restingPanelFilters, this.appliedPanelFilters);
		},
		draftFilterCount (): number {
			return countPanelFilters(this.restingPanelFilters, this.draftFilters);
		},
	},
	created () {
		this.draftFilters = normalizePanelFilters(this.restingPanelFilters, this.appliedPanelFilters);
	},
	methods: {
		toggleFilters () {
			this.filtersExpanded = !this.filtersExpanded;
		},
		/** Commit the draft, close the box, and fetch once, unless nothing changed. */
		applyFilters () {
			const next: PanelFilters = normalizePanelFilters(this.restingPanelFilters, this.draftFilters);
			this.filtersExpanded = false;
			if (samePanelFilters(this.restingPanelFilters, next, this.appliedPanelFilters)) return;
			this.commitPanelFilters(next);
		},
		/** Throw the draft away: it goes back to what is applied, and the box closes. */
		cancelFilters () {
			this.draftFilters = normalizePanelFilters(this.restingPanelFilters, this.appliedPanelFilters);
			this.filtersExpanded = false;
		},
		/** Empty the draft and apply that at once, leaving the box open. */
		clearFilters () {
			const resting: PanelFilters = normalizePanelFilters(this.restingPanelFilters, null);
			this.draftFilters = resting;
			if (!this.appliedFilterCount) return;
			this.commitPanelFilters(resting);
		},
		/**
		 * After the applied filters were restored from outside the box (the view
		 * remembered from the last visit, or the route moving to another team):
		 * the draft follows them, and the box starts closed. The Filter button's
		 * count already says the list is narrowed.
		 */
		syncFiltersPanel () {
			this.draftFilters = normalizePanelFilters(this.restingPanelFilters, this.appliedPanelFilters);
			this.filtersExpanded = false;
		},
	},
});
